/**
 * PATH: packages/email-hub/src/feature/components/email-reader/ReaderWorkflowSections.tsx
 * FILE_ID: EMAILHUB-EMAILREADER-WORKFLOW-SECTIONS-001
 * PURPOSE: Render triage, allocation, extraction, attachment, and linked-file states.
 */
import { useEffect, useRef } from 'react';
import { Cloud, Paperclip } from 'lucide-react';
import { cn } from '../../../_internal/lib/utils';
import { isEmailHubFeatureEnabled } from '../../../_internal/flags';
import CalendarInviteCard from '../CalendarInviteCard';
import ExternalLinkChip from '../ExternalLinkChip';
import { PingNotificationCard } from '../PingNotificationCard';
import { usePingHandoff } from '../usePingHandoff';
import { QuoteRequestDetailsBlock } from '../QuoteRequestDetailsBlock';
import type { EmailReaderController } from './useEmailReaderController';

function AttachmentHydratingPlaceholder({ onRefetch }: { onRefetch: () => void }) {
  const triggered = useRef(false);
  useEffect(() => {
    if (triggered.current) return;
    triggered.current = true;
    const id = window.setTimeout(onRefetch, 1500);
    return () => window.clearTimeout(id);
  }, [onRefetch]);

  return (
    <div className="bg-muted/30 rounded-xl p-4">
      <h3 className="font-medium text-foreground mb-3 flex items-center gap-2 text-sm">
        <Paperclip className="h-4 w-4" />
        Loading attachments…
      </h3>
      <div className="grid grid-cols-2 gap-2">
        {[0, 1].map((index) => (
          <div
            key={index}
            className="flex items-center justify-between p-3 bg-background rounded-lg border border-border animate-pulse"
          >
            <div className="flex items-center gap-3 min-w-0 w-full">
              <div className="h-9 w-9 rounded-lg bg-muted shrink-0" />
              <div className="min-w-0 flex-1 space-y-1.5">
                <div className="h-3 bg-muted rounded w-3/4" />
                <div className="h-2 bg-muted rounded w-1/3" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ReaderWorkflowSections({ controller }: { controller: EmailReaderController }) {
  const { email, refetch, useDraftReply } = controller;
  // Called unconditionally (rules of hooks) even though the card below only
  // renders flag-OFF — `usePingHandoff('')` is a documented no-op, so this
  // never fetches once the whole-header flag flips this surface off for
  // good (change #269: PingNotificationCard no longer self-fetches).
  const pingHandoff = usePingHandoff(email?.id ?? '');
  if (!email) return null;

  return (
    <>
      {/* Flag-OFF safety (change #269): whole header renders this card itself. */}
      {!isEmailHubFeatureEnabled('wholeReaderHeader') && (
        <PingNotificationCard
          emailId={email.id}
          allocationSummary={email.allocation_summary}
          handoff={pingHandoff}
        />
      )}
      {email.quote_request_extraction && (
        <QuoteRequestDetailsBlock
          extraction={email.quote_request_extraction}
          emailId={email.id}
          onUseDraftReply={useDraftReply}
        />
      )}
      {email.has_attachments && (email.attachments?.length ?? 0) === 0 && (
        <div className="mb-6">
          <AttachmentHydratingPlaceholder onRefetch={refetch} />
        </div>
      )}
      {Array.isArray(email.external_links) && email.external_links.length > 0 && (
        <div
          className={cn(
            'rounded-xl border border-border bg-gradient-to-b from-muted/40 to-muted/10',
            'p-4 mb-6'
          )}
          data-testid="email-external-links-block"
        >
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-2 min-w-0">
              <div className="h-7 w-7 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
                <Cloud className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground leading-tight">
                  {email.external_links.length} linked file
                  {email.external_links.length === 1 ? '' : 's'}
                </p>
                <p className="text-[11px] text-muted-foreground leading-tight">
                  Auto-downloaded into QDrive · the link in the body always works
                </p>
              </div>
            </div>
          </div>
          <div className="grid gap-2 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3">
            {email.external_links.map((link) => (
              <ExternalLinkChip key={link.id} link={link} emailId={email.id} />
            ))}
          </div>
        </div>
      )}
      {email.calendar_event && (
        <div className="mb-6">
          <CalendarInviteCard event={email.calendar_event} />
        </div>
      )}
    </>
  );
}
